import { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, TrendingUp, TrendingDown, Minus, LogOut, Search, Languages, Sparkles, Settings, CreditCard, Zap } from "lucide-react";
import { TiaScalpingPage } from "@/components/strategies/TiaScalpingPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = "https://static.prod-images.emergentagent.com/jobs/75789c9b-0d29-4ec0-9a3b-e1fc05fe9785/images/5b2ab7fd29746777696641592f7d349c0b2930b2e79250b8a28c9a3f926de55d.png";

// FIXED inputs for Basic Tier — DO NOT expose any market or timeframe controls.
const FIXED_SYMBOL = "XAU/USD";
const FIXED_TF = "15min";

export function BasicTierView({ user, token, lang, onLogout, toggleLanguage, membershipData, onPayPal, onAdmin }) {
  const isAr = lang === "ar";
  const [tab, setTab] = useState("combo");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const analyze = async () => {
    setLoading(true);
    setError("");
    try {
      const urlSymbol = FIXED_SYMBOL.replace("/", "-");
      const res = await axios.get(`${API}/advanced-combination/${urlSymbol}`, {
        params: { timeframe: FIXED_TF },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || (isAr ? "تعذّر جلب الإشارة. حاول مجدداً." : "Failed to load signal. Try again."));
    } finally {
      setLoading(false);
    }
  };

  const sig = data?.signal || "NEUTRAL";
  const sigColor = sig === "BUY" ? "bg-emerald-500" : sig === "SELL" ? "bg-red-500" : "bg-zinc-400";
  const SigIcon = sig === "BUY" ? TrendingUp : sig === "SELL" ? TrendingDown : Minus;

  return (
    <div dir={isAr ? "rtl" : "ltr"} className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-cyan-100">
      {/* Slim header */}
      <div className="bg-gradient-to-r from-pink-200 via-yellow-100 to-cyan-200 border-b-4 border-pink-300 shadow-md">
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 max-w-3xl mx-auto">
          <div className="flex items-center gap-2 shrink-0">
            <img src="/logo.png" alt="TTANALYSIS" className="w-8 h-8 sm:w-10 sm:h-10 drop-shadow" />
            <span className="text-sm sm:text-lg font-extrabold text-pink-600 tracking-wide">TTANALYSIS</span>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {membershipData && (
              <Badge className={`text-[10px] sm:text-[11px] px-2 sm:px-3 py-0.5 sm:py-1 rounded-full font-semibold ${
                membershipData.membership_active
                  ? "bg-gradient-to-r from-emerald-400 to-green-500 text-white"
                  : "bg-gradient-to-r from-red-400 to-pink-500 text-white"
              }`} data-testid="membership-badge">
                {membershipData.membership_active ? (membershipData.time_remaining || (isAr ? "نشط" : "Active")) : (isAr ? "منتهي" : "Expired")}
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] border-pink-400 text-pink-700 bg-pink-50">
              {isAr ? "باقة Basic" : "Basic Tier"}
            </Badge>
            <Button variant="outline" size="sm" onClick={toggleLanguage} className="h-8 border-pink-300 text-pink-700 bg-white text-xs" data-testid="basic-lang-toggle">
              <Languages className="w-3.5 h-3.5 mr-1" />
              {isAr ? "EN" : "عربي"}
            </Button>
            {membershipData && !membershipData.membership_active && onPayPal && (
              <button
                onClick={onPayPal}
                className="text-[10px] px-2 py-1 rounded-full bg-gradient-to-r from-blue-400 to-indigo-500 hover:from-blue-500 hover:to-indigo-600 text-white font-semibold shadow"
                data-testid="paypal-btn"
              >
                <CreditCard className="w-3 h-3 inline mr-0.5" />$20
              </button>
            )}
            {user?.role === "admin" && onAdmin && (
              <button onClick={onAdmin} className="p-1.5 rounded-full bg-white/80 hover:bg-pink-100 text-pink-600 border-2 border-pink-200" data-testid="admin-btn">
                <Settings className="w-3.5 h-3.5" />
              </button>
            )}
            <button onClick={onLogout} className="p-1.5 rounded-full bg-white/80 hover:bg-red-100 text-red-500 border-2 border-red-200" data-testid="logout-btn">
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Main card */}
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        {/* Tab switcher */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setTab("combo")}
            data-testid="basic-tab-combo"
            className={`flex-1 py-2.5 px-3 rounded-2xl text-sm font-bold transition-all ${
              tab === "combo"
                ? "bg-pink-500 text-white shadow-lg shadow-pink-300/50"
                : "bg-white text-pink-600 border-2 border-pink-200"
            }`}
          >
            <Sparkles className="w-4 h-4 inline mr-1.5" />
            {isAr ? "كومبو الذهب" : "Gold Combo"}
          </button>
          <button
            onClick={() => setTab("tia")}
            data-testid="basic-tab-tia"
            className={`flex-1 py-2.5 px-3 rounded-2xl text-sm font-bold transition-all ${
              tab === "tia"
                ? "bg-purple-500 text-white shadow-lg shadow-purple-300/50"
                : "bg-white text-purple-600 border-2 border-purple-200"
            }`}
          >
            <Zap className="w-4 h-4 inline mr-1.5" />
            {isAr ? "سكالب تيا" : "Tia Scalp"}
          </button>
        </div>

        {tab === "tia" && (
          <TiaScalpingPage lang={lang} user={user} simpleOnly={true} />
        )}

        {tab === "combo" && (<>
        <Card className="bg-white border-4 border-pink-300 rounded-3xl shadow-xl shadow-pink-200/40 overflow-hidden">
          <div className="h-2 bg-gradient-to-r from-amber-400 via-pink-400 to-purple-500" />
          <CardContent className="p-5 sm:p-8 text-center">
            <img src={LOGO} alt="" className="w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-3 rounded-full border-4 border-pink-300 shadow-lg" />
            <h1 className="text-xl sm:text-2xl font-extrabold text-pink-600 mb-1">
              {isAr ? `أهلاً ${user?.name || ""}` : `Welcome ${user?.name || ""}`}
            </h1>
            <p className="text-sm text-zinc-600 mb-1">
              {isAr ? "باقتك تمنحك إشارة Combination للذهب على فريم 15 دقيقة فقط" : "Your plan unlocks the Gold Combination signal on the 15-minute timeframe only"}
            </p>
            <p className="text-[11px] text-zinc-400 mb-5">
              {isAr ? "السوق: XAU/USD · الفريم: 15min (ثابتان)" : "Market: XAU/USD · Timeframe: 15min (fixed)"}
            </p>

            <Button
              onClick={analyze}
              disabled={loading}
              size="lg"
              className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold px-8 py-6 text-base rounded-full shadow-lg shadow-emerald-200/50"
              data-testid="basic-analyze-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Search className="w-5 h-5 mr-2" />}
              {isAr ? "حلّل الذهب الآن" : "Analyze Gold Now"}
            </Button>

            {error && (
              <div className="mt-4 bg-red-50 border-2 border-red-200 text-red-700 p-3 rounded-2xl text-sm" data-testid="basic-error">
                {error}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signal result */}
        {data && (
          <Card className="mt-4 bg-white border-4 border-pink-300 rounded-3xl shadow-xl shadow-pink-200/40 overflow-hidden" data-testid="basic-signal-card">
            <div className={`h-2 ${sigColor}`} />
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-pink-500" />
                  <h3 className="font-bold text-sm sm:text-base text-zinc-800">
                    {isAr ? "إشارة Combination للذهب" : "Gold Combination Signal"}
                  </h3>
                  <Badge variant="outline" className="text-[10px] border-pink-300 text-pink-600">XAU/USD · 15min</Badge>
                </div>
                <Button variant="ghost" size="icon" onClick={analyze} disabled={loading} className="text-pink-500 h-8 w-8">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </Button>
              </div>

              <div className="flex items-center justify-between mb-4">
                <Badge className={`${sigColor} text-white text-base sm:text-lg px-4 py-2 rounded-full font-bold`}>
                  <SigIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-1.5" />
                  {sig}
                </Badge>
                <div className="text-right">
                  <p className="text-[10px] uppercase text-zinc-500 tracking-wide">{isAr ? "الثقة" : "Confidence"}</p>
                  <p className="text-xl sm:text-2xl font-bold text-pink-600">{data.confidence}%</p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                <Box label={isAr ? "السعر الحالي" : "Current"} value={data.current_price} color="zinc" />
                <Box label="ATR" value={data.atr} color="zinc" />
                <Box label={isAr ? "الإشارة" : "Signal"} value={data.signal} color={sig === "BUY" ? "emerald" : sig === "SELL" ? "red" : "zinc"} />
                <Box label={isAr ? "الثقة" : "Confidence"} value={`${data.confidence}%`} color="blue" />
              </div>

              {/* Techniques agreement count (Basic-visible summary only, no names) */}
              {(typeof data.buy_count === "number" || typeof data.sell_count === "number") && (
                <div className="mb-3 grid grid-cols-2 gap-2" data-testid="basic-agreement">
                  <div className={`rounded-2xl border-2 p-2.5 text-center ${sig === "BUY" ? "bg-emerald-50 border-emerald-300" : sig === "SELL" ? "bg-red-50 border-red-300" : "bg-zinc-50 border-zinc-200"}`}>
                    <p className="text-[10px] uppercase tracking-wide text-zinc-500">
                      {isAr ? "مؤشرات متفقة" : "Techniques Agreeing"}
                    </p>
                    <p className={`text-lg font-bold ${sig === "BUY" ? "text-emerald-700" : sig === "SELL" ? "text-red-700" : "text-zinc-700"}`}>
                      {sig === "BUY" ? data.buy_count : sig === "SELL" ? data.sell_count : Math.max(data.buy_count || 0, data.sell_count || 0)}<span className="text-zinc-400 text-sm">/5</span>
                    </p>
                    <p className="text-[10px] text-zinc-500 mt-0.5">{sig !== "NEUTRAL" ? sig : (isAr ? "لا اتفاق" : "no consensus")}</p>
                  </div>
                  <div className="rounded-2xl border-2 border-blue-200 bg-blue-50 p-2.5 text-center">
                    <p className="text-[10px] uppercase tracking-wide text-zinc-500">
                      {isAr ? "الثقة" : "Confidence"}
                    </p>
                    <p className="text-lg font-bold text-blue-700">{data.confidence}%</p>
                    {(data.buy_count > 0 && data.sell_count > 0) && (
                      <p className="text-[10px] text-red-600 mt-0.5 font-semibold">
                        {isAr ? `⚠ ${sig === "BUY" ? data.sell_count : data.buy_count} عكس الاتجاه` : `⚠ ${sig === "BUY" ? data.sell_count : data.buy_count} disagree`}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {data.entry_aggressive && (
                <div className="border-2 border-amber-300 bg-amber-50 rounded-2xl p-3 mb-2" data-testid="basic-entry-aggressive">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-amber-700">
                      {isAr ? "دخول جريء (0.3×ATR)" : "Aggressive Entry (0.3×ATR)"}
                    </span>
                    <Badge variant="outline" className="text-[9px] border-amber-400 text-amber-700">{isAr ? "مبكر" : "Early"}</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <Field k="Entry" v={data.entry_aggressive.price} bold />
                    <Field k="SL" v={data.entry_aggressive.stop_loss} color="text-red-600" />
                    <Field k="TP1" v={data.entry_aggressive.take_profit_1} color="text-emerald-600" />
                    <Field k="TP2" v={data.entry_aggressive.take_profit_2} color="text-emerald-600" />
                  </div>
                </div>
              )}

              {data.entry_conservative && (
                <div className="border-2 border-cyan-300 bg-cyan-50 rounded-2xl p-3 mb-2" data-testid="basic-entry-conservative">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-bold text-cyan-700">
                      {isAr ? "دخول متحفظ (0.7×ATR)" : "Conservative Entry (0.7×ATR)"}
                    </span>
                    <Badge variant="outline" className="text-[9px] border-cyan-400 text-cyan-700">{isAr ? "آمن" : "Safer"}</Badge>
                  </div>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    <Field k="Entry" v={data.entry_conservative.price} bold />
                    <Field k="SL" v={data.entry_conservative.stop_loss} color="text-red-600" />
                    <Field k="TP1" v={data.entry_conservative.take_profit_1} color="text-emerald-600" />
                    <Field k="TP2" v={data.entry_conservative.take_profit_2} color="text-emerald-600" />
                  </div>
                </div>
              )}

              {typeof data.confidence === "number" && data.confidence < 70 && (
                <div className="mt-2 bg-amber-50 border-2 border-amber-300 rounded-xl p-3 text-xs text-amber-800" data-testid="basic-risk-notice">
                  <p className="font-bold mb-1">
                    {isAr ? "⚠ تحذير مخاطرة" : "⚠ Risk Warning"}
                  </p>
                  <p>
                    {isAr
                      ? `الثقة في هذه الإشارة منخفضة (${data.confidence}%). يُفضّل الانتظار لإشارة أقوى أو تقليل حجم الصفقة.`
                      : `Signal confidence is low (${data.confidence}%). Consider waiting for a stronger setup or reducing position size.`}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        </>)}
      </div>
    </div>
  );
}

function Box({ label, value, color = "zinc" }) {
  const colorMap = {
    zinc: "bg-zinc-50 border-zinc-200 text-zinc-800",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    red: "bg-red-50 border-red-200 text-red-700",
  };
  return (
    <div className={`rounded-xl border p-2 text-center ${colorMap[color]}`}>
      <p className="text-[10px] uppercase tracking-wide opacity-70">{label}</p>
      <p className="text-xs sm:text-sm font-mono font-bold">{value ?? "-"}</p>
    </div>
  );
}

function Field({ k, v, color = "text-zinc-800", bold = false }) {
  return (
    <div>
      <span className="text-zinc-500 block">{k}</span>
      <span className={`font-mono ${bold ? "font-bold" : ""} ${color}`}>{v ?? "-"}</span>
    </div>
  );
}
